/**
 * Curriculum Planning Store
 *
 * Zustand store for all curriculum planning data.
 * Replaces Convex queries and mutations with local state management.
 */

import { create } from 'zustand';
import { generateId } from '../lib/utils/idGenerator';
import type {
  CpAcademicYear,
  CpTerm,
  CpKeyStage,
  CpYearGroup,
  CpTimetableCycle,
  CpPeriod,
  CpSubjectCategory,
  CpSubject,
  CpSubjectSlot,
  CpSubjectSlotSubject,
  CpStaffRole,
  CpTeacher,
  CpTeacherRole,
  CpTeacherSubject,
  CpRoomType,
  CpRoom,
  CpBand,
  CpSet,
  CpClass,
  CpOptionBlock,
  CpOptionBlockSubject,
  CpRotationGroup,
  CpRotationGroupSubject,
  CpCurriculumPlan,
  CpYearGroupWithKeyStage,
  CpSubjectWithCategory,
  CpSubjectSlotWithSubjects,
  CpTeacherWithRoles,
  CpOptionBlockWithSubjects,
  CreateCpAcademicYearInput,
  CreateCpTermInput,
  CreateCpTimetableCycleInput,
  CreateCpSubjectInput,
  CreateCpSubjectSlotInput,
  CreateCpTeacherInput,
  CreateCpBandInput,
  CreateCpSetInput,
  CreateCpClassInput,
  CreateCpOptionBlockInput,
  CreateCpCurriculumPlanInput,
} from '../types/curriculum_planning';
import {
  mockCpAcademicYears,
  mockCpTerms,
  mockCpKeyStages,
  mockCpYearGroups,
  mockCpTimetableCycles,
  mockCpPeriods,
  mockCpSubjectCategories,
  mockCpSubjects,
  mockCpSubjectSlots,
  mockCpSubjectSlotSubjects,
  mockCpStaffRoles,
  mockCpTeachers,
  mockCpTeacherRoles,
  mockCpTeacherSubjects,
  mockCpRoomTypes,
  mockCpRooms,
  mockCpBands,
  mockCpSets,
  mockCpClasses,
  mockCpOptionBlocks,
  mockCpOptionBlockSubjects,
  mockCpRotationGroups,
  mockCpRotationGroupSubjects,
  mockCpCurriculumPlans,
} from '../lib/mockData/curriculumPlanning';

// ============================================================================
// State Interface
// ============================================================================

interface CurriculumPlanningState {
  // Academic Calendar
  academicYears: CpAcademicYear[];
  terms: CpTerm[];
  keyStages: CpKeyStage[];
  yearGroups: CpYearGroup[];
  timetableCycles: CpTimetableCycle[];
  periods: CpPeriod[];

  // Subjects
  subjectCategories: CpSubjectCategory[];
  subjects: CpSubject[];
  subjectSlots: CpSubjectSlot[];
  subjectSlotSubjects: CpSubjectSlotSubject[];

  // Staff
  staffRoles: CpStaffRole[];
  teachers: CpTeacher[];
  teacherRoles: CpTeacherRole[];
  teacherSubjects: CpTeacherSubject[];

  // Rooms
  roomTypes: CpRoomType[];
  rooms: CpRoom[];

  // Groupings
  bands: CpBand[];
  sets: CpSet[];
  classes: CpClass[];

  // Option Blocks & Rotations
  optionBlocks: CpOptionBlock[];
  optionBlockSubjects: CpOptionBlockSubject[];
  rotationGroups: CpRotationGroup[];
  rotationGroupSubjects: CpRotationGroupSubject[];

  // Curriculum Plans
  curriculumPlans: CpCurriculumPlan[];

  // ============================================================================
  // Getters - Academic Calendar
  // ============================================================================

  getActiveAcademicYear: () => CpAcademicYear | undefined;
  getAcademicYearById: (id: string) => CpAcademicYear | undefined;
  getTermsByAcademicYear: (academicYearId: string) => CpTerm[];
  getTimetableCyclesByAcademicYear: (academicYearId: string) => CpTimetableCycle[];
  getPeriodsByTimetableCycle: (timetableCycleId: string) => CpPeriod[];

  // ============================================================================
  // Getters - Year Groups & Key Stages
  // ============================================================================

  getYearGroupsWithKeyStage: () => CpYearGroupWithKeyStage[];
  getYearGroupById: (id: string) => CpYearGroup | undefined;
  getYearGroupsByKeyStage: (keyStageId: string) => CpYearGroup[];

  // ============================================================================
  // Getters - Subjects
  // ============================================================================

  getSubjectsWithCategory: () => CpSubjectWithCategory[];
  getActiveSubjects: () => CpSubject[];
  getSubjectById: (id: string) => CpSubject | undefined;
  getSubjectsByCategory: (categoryId: string) => CpSubject[];
  getSubjectSlotsWithSubjects: () => CpSubjectSlotWithSubjects[];

  // ============================================================================
  // Getters - Teachers
  // ============================================================================

  getTeachersWithRoles: () => CpTeacherWithRoles[];
  getActiveTeachers: () => CpTeacher[];
  getTeacherById: (id: string) => CpTeacher | undefined;
  getTeacherSubjects: (teacherId: string) => CpSubject[];

  // ============================================================================
  // Getters - Rooms
  // ============================================================================

  getActiveRooms: () => CpRoom[];
  getRoomsByType: (roomTypeId: string) => CpRoom[];

  // ============================================================================
  // Getters - Groupings
  // ============================================================================

  getBandsByYearAndAcademic: (yearGroupId: string, academicYearId: string) => CpBand[];
  getSetsByYearAndAcademic: (yearGroupId: string, academicYearId: string) => CpSet[];
  getSetsByBand: (bandId: string) => CpSet[];
  getClassesByYearAndAcademic: (yearGroupId: string, academicYearId: string) => CpClass[];
  getClassesByBand: (bandId: string) => CpClass[];

  // ============================================================================
  // Getters - Option Blocks
  // ============================================================================

  getOptionBlocksByYearAndAcademic: (yearGroupId: string, academicYearId: string) => CpOptionBlock[];
  getOptionBlocksWithSubjects: (yearGroupId: string, academicYearId: string) => CpOptionBlockWithSubjects[];
  getOptionBlockSubjects: (optionBlockId: string) => CpSubject[];

  // ============================================================================
  // Getters - Curriculum Plans
  // ============================================================================

  getCurriculumPlansByYearAndAcademic: (yearGroupId: string, academicYearId: string) => CpCurriculumPlan[];
  getCurriculumPlansBySubject: (subjectId: string) => CpCurriculumPlan[];

  // ============================================================================
  // Mutations - Academic Calendar
  // ============================================================================

  addAcademicYear: (data: CreateCpAcademicYearInput) => string;
  updateAcademicYear: (id: string, updates: Partial<CpAcademicYear>) => void;
  setActiveAcademicYear: (id: string) => void;
  deleteAcademicYear: (id: string) => void;

  addTerm: (data: CreateCpTermInput) => string;
  updateTerm: (id: string, updates: Partial<CpTerm>) => void;
  deleteTerm: (id: string) => void;

  addTimetableCycle: (data: CreateCpTimetableCycleInput) => string;
  updateTimetableCycle: (id: string, updates: Partial<CpTimetableCycle>) => void;
  deleteTimetableCycle: (id: string) => void;

  // ============================================================================
  // Mutations - Subjects
  // ============================================================================

  addSubject: (data: CreateCpSubjectInput) => string;
  updateSubject: (id: string, updates: Partial<CpSubject>) => void;
  archiveSubject: (id: string) => void;
  restoreSubject: (id: string) => void;

  addSubjectSlot: (data: CreateCpSubjectSlotInput) => string;
  updateSubjectSlot: (id: string, updates: Partial<CpSubjectSlot>) => void;
  addSubjectToSlot: (slotId: string, subjectId: string) => void;
  removeSubjectFromSlot: (slotId: string, subjectId: string) => void;

  // ============================================================================
  // Mutations - Teachers
  // ============================================================================

  addTeacher: (data: CreateCpTeacherInput) => string;
  updateTeacher: (id: string, updates: Partial<CpTeacher>) => void;
  archiveTeacher: (id: string) => void;
  restoreTeacher: (id: string) => void;

  // ============================================================================
  // Mutations - Groupings
  // ============================================================================

  addBand: (data: CreateCpBandInput) => string;
  updateBand: (id: string, updates: Partial<CpBand>) => void;
  deleteBand: (id: string) => void;

  addSet: (data: CreateCpSetInput) => string;
  updateSet: (id: string, updates: Partial<CpSet>) => void;
  deleteSet: (id: string) => void;

  addClass: (data: CreateCpClassInput) => string;
  updateClass: (id: string, updates: Partial<CpClass>) => void;
  deleteClass: (id: string) => void;

  // ============================================================================
  // Mutations - Option Blocks
  // ============================================================================

  addOptionBlock: (data: CreateCpOptionBlockInput) => string;
  updateOptionBlock: (id: string, updates: Partial<CpOptionBlock>) => void;
  deleteOptionBlock: (id: string) => void;
  addSubjectToOptionBlock: (blockId: string, subjectId: string, maxStudents?: number, teacherId?: string) => void;
  removeSubjectFromOptionBlock: (blockId: string, subjectId: string) => void;

  // ============================================================================
  // Mutations - Curriculum Plans
  // ============================================================================

  addCurriculumPlan: (data: CreateCpCurriculumPlanInput) => string;
  updateCurriculumPlan: (id: string, updates: Partial<CpCurriculumPlan>) => void;
  deleteCurriculumPlan: (id: string) => void;

  // ============================================================================
  // Bulk Operations
  // ============================================================================

  resetAllData: () => void;
  importData: (data: Partial<CurriculumPlanningState>) => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useCurriculumPlanningStore = create<CurriculumPlanningState>((set, get) => ({
  // Initialize with mock data
  academicYears: mockCpAcademicYears,
  terms: mockCpTerms,
  keyStages: mockCpKeyStages,
  yearGroups: mockCpYearGroups,
  timetableCycles: mockCpTimetableCycles,
  periods: mockCpPeriods,
  subjectCategories: mockCpSubjectCategories,
  subjects: mockCpSubjects,
  subjectSlots: mockCpSubjectSlots,
  subjectSlotSubjects: mockCpSubjectSlotSubjects,
  staffRoles: mockCpStaffRoles,
  teachers: mockCpTeachers,
  teacherRoles: mockCpTeacherRoles,
  teacherSubjects: mockCpTeacherSubjects,
  roomTypes: mockCpRoomTypes,
  rooms: mockCpRooms,
  bands: mockCpBands,
  sets: mockCpSets,
  classes: mockCpClasses,
  optionBlocks: mockCpOptionBlocks,
  optionBlockSubjects: mockCpOptionBlockSubjects,
  rotationGroups: mockCpRotationGroups,
  rotationGroupSubjects: mockCpRotationGroupSubjects,
  curriculumPlans: mockCpCurriculumPlans,

  // ============================================================================
  // Getters - Academic Calendar
  // ============================================================================

  getActiveAcademicYear: () => {
    return get().academicYears.find((ay) => ay.isActive);
  },

  getAcademicYearById: (id) => {
    return get().academicYears.find((ay) => ay.id === id);
  },

  getTermsByAcademicYear: (academicYearId) => {
    return get()
      .terms.filter((t) => t.academicYearId === academicYearId)
      .sort((a, b) => a.termNumber - b.termNumber);
  },

  getTimetableCyclesByAcademicYear: (academicYearId) => {
    return get().timetableCycles.filter((tc) => tc.academicYearId === academicYearId);
  },

  getPeriodsByTimetableCycle: (timetableCycleId) => {
    return get()
      .periods.filter((p) => p.timetableCycleId === timetableCycleId)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.ordinalPosition - b.ordinalPosition);
  },

  // ============================================================================
  // Getters - Year Groups & Key Stages
  // ============================================================================

  getYearGroupsWithKeyStage: () => {
    const { yearGroups, keyStages } = get();
    return yearGroups
      .map((yg) => ({
        ...yg,
        keyStage: keyStages.find((ks) => ks.id === yg.keyStageId),
      }))
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getYearGroupById: (id) => {
    return get().yearGroups.find((yg) => yg.id === id);
  },

  getYearGroupsByKeyStage: (keyStageId) => {
    return get()
      .yearGroups.filter((yg) => yg.keyStageId === keyStageId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  // ============================================================================
  // Getters - Subjects
  // ============================================================================

  getSubjectsWithCategory: () => {
    const { subjects, subjectCategories } = get();
    return subjects
      .map((s) => ({
        ...s,
        category: subjectCategories.find((sc) => sc.id === s.categoryId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  getActiveSubjects: () => {
    return get()
      .subjects.filter((s) => s.isActive)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  getSubjectById: (id) => {
    return get().subjects.find((s) => s.id === id);
  },

  getSubjectsByCategory: (categoryId) => {
    return get()
      .subjects.filter((s) => s.categoryId === categoryId)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  getSubjectSlotsWithSubjects: () => {
    const { subjectSlots, subjectSlotSubjects, subjects } = get();
    return subjectSlots.map((slot) => {
      const slotSubjectIds = subjectSlotSubjects
        .filter((sss) => sss.subjectSlotId === slot.id)
        .map((sss) => sss.subjectId);
      return {
        ...slot,
        subjects: subjects.filter((s) => slotSubjectIds.includes(s.id)),
      };
    });
  },

  // ============================================================================
  // Getters - Teachers
  // ============================================================================

  getTeachersWithRoles: () => {
    const { teachers, teacherRoles, staffRoles } = get();
    return teachers.map((teacher) => {
      const tRoles = teacherRoles.filter((tr) => tr.teacherId === teacher.id);
      const roles = tRoles.map((tr) => {
        const role = staffRoles.find((sr) => sr.id === tr.roleId);
        return role ? { ...role, isPrimary: tr.isPrimaryRole } : null;
      }).filter(Boolean) as Array<CpStaffRole & { isPrimary: boolean }>;
      return { ...teacher, roles };
    });
  },

  getActiveTeachers: () => {
    return get()
      .teachers.filter((t) => t.isActive)
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  },

  getTeacherById: (id) => {
    return get().teachers.find((t) => t.id === id);
  },

  getTeacherSubjects: (teacherId) => {
    const { teacherSubjects, subjects } = get();
    const subjectIds = teacherSubjects.filter((ts) => ts.teacherId === teacherId).map((ts) => ts.subjectId);
    return subjects.filter((s) => subjectIds.includes(s.id));
  },

  // ============================================================================
  // Getters - Rooms
  // ============================================================================

  getActiveRooms: () => {
    return get().rooms.filter((r) => r.isActive);
  },

  getRoomsByType: (roomTypeId) => {
    return get().rooms.filter((r) => r.roomTypeId === roomTypeId);
  },

  // ============================================================================
  // Getters - Groupings
  // ============================================================================

  getBandsByYearAndAcademic: (yearGroupId, academicYearId) => {
    return get()
      .bands.filter((b) => b.yearGroupId === yearGroupId && b.academicYearId === academicYearId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getSetsByYearAndAcademic: (yearGroupId, academicYearId) => {
    return get()
      .sets.filter((s) => s.yearGroupId === yearGroupId && s.academicYearId === academicYearId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getSetsByBand: (bandId) => {
    return get()
      .sets.filter((s) => s.bandId === bandId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getClassesByYearAndAcademic: (yearGroupId, academicYearId) => {
    return get()
      .classes.filter((c) => c.yearGroupId === yearGroupId && c.academicYearId === academicYearId)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  getClassesByBand: (bandId) => {
    return get()
      .classes.filter((c) => c.bandId === bandId)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  // ============================================================================
  // Getters - Option Blocks
  // ============================================================================

  getOptionBlocksByYearAndAcademic: (yearGroupId, academicYearId) => {
    return get()
      .optionBlocks.filter((ob) => ob.yearGroupId === yearGroupId && ob.academicYearId === academicYearId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getOptionBlocksWithSubjects: (yearGroupId, academicYearId) => {
    const { optionBlocks, optionBlockSubjects, subjects } = get();
    return optionBlocks
      .filter((ob) => ob.yearGroupId === yearGroupId && ob.academicYearId === academicYearId)
      .map((ob) => {
        const blockSubjectIds = optionBlockSubjects
          .filter((obs) => obs.optionBlockId === ob.id)
          .map((obs) => obs.subjectId);
        return {
          ...ob,
          subjects: subjects.filter((s) => blockSubjectIds.includes(s.id)),
        };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  },

  getOptionBlockSubjects: (optionBlockId) => {
    const { optionBlockSubjects, subjects } = get();
    const subjectIds = optionBlockSubjects
      .filter((obs) => obs.optionBlockId === optionBlockId)
      .map((obs) => obs.subjectId);
    return subjects.filter((s) => subjectIds.includes(s.id));
  },

  // ============================================================================
  // Getters - Curriculum Plans
  // ============================================================================

  getCurriculumPlansByYearAndAcademic: (yearGroupId, academicYearId) => {
    return get().curriculumPlans.filter(
      (cp) => cp.yearGroupId === yearGroupId && cp.academicYearId === academicYearId
    );
  },

  getCurriculumPlansBySubject: (subjectId) => {
    return get().curriculumPlans.filter((cp) => cp.subjectId === subjectId);
  },

  // ============================================================================
  // Mutations - Academic Calendar
  // ============================================================================

  addAcademicYear: (data) => {
    const id = generateId('ay');
    const now = Date.now();
    const newYear: CpAcademicYear = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ academicYears: [...state.academicYears, newYear] }));
    return id;
  },

  updateAcademicYear: (id, updates) => {
    set((state) => ({
      academicYears: state.academicYears.map((ay) =>
        ay.id === id ? { ...ay, ...updates, updatedAt: Date.now() } : ay
      ),
    }));
  },

  setActiveAcademicYear: (id) => {
    set((state) => ({
      academicYears: state.academicYears.map((ay) => ({
        ...ay,
        isActive: ay.id === id,
        updatedAt: Date.now(),
      })),
    }));
  },

  deleteAcademicYear: (id) => {
    set((state) => ({
      academicYears: state.academicYears.filter((ay) => ay.id !== id),
      terms: state.terms.filter((t) => t.academicYearId !== id),
      timetableCycles: state.timetableCycles.filter((tc) => tc.academicYearId !== id),
    }));
  },

  addTerm: (data) => {
    const id = generateId('term');
    const now = Date.now();
    const newTerm: CpTerm = { ...data, id, createdAt: now };
    set((state) => ({ terms: [...state.terms, newTerm] }));
    return id;
  },

  updateTerm: (id, updates) => {
    set((state) => ({
      terms: state.terms.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    }));
  },

  deleteTerm: (id) => {
    set((state) => ({ terms: state.terms.filter((t) => t.id !== id) }));
  },

  addTimetableCycle: (data) => {
    const id = generateId('tc');
    const now = Date.now();
    const totalPeriodsPerCycle = data.periodsPerDay * data.daysPerWeek * (data.cycleType === '2-week' ? 2 : 1);
    const newCycle: CpTimetableCycle = {
      ...data,
      id,
      totalPeriodsPerCycle,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ timetableCycles: [...state.timetableCycles, newCycle] }));
    return id;
  },

  updateTimetableCycle: (id, updates) => {
    set((state) => ({
      timetableCycles: state.timetableCycles.map((tc) =>
        tc.id === id ? { ...tc, ...updates, updatedAt: Date.now() } : tc
      ),
    }));
  },

  deleteTimetableCycle: (id) => {
    set((state) => ({
      timetableCycles: state.timetableCycles.filter((tc) => tc.id !== id),
      periods: state.periods.filter((p) => p.timetableCycleId !== id),
    }));
  },

  // ============================================================================
  // Mutations - Subjects
  // ============================================================================

  addSubject: (data) => {
    const id = generateId('subj');
    const now = Date.now();
    const newSubject: CpSubject = {
      ...data,
      id,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ subjects: [...state.subjects, newSubject] }));
    return id;
  },

  updateSubject: (id, updates) => {
    set((state) => ({
      subjects: state.subjects.map((s) =>
        s.id === id ? { ...s, ...updates, updatedAt: Date.now() } : s
      ),
    }));
  },

  archiveSubject: (id) => {
    set((state) => ({
      subjects: state.subjects.map((s) =>
        s.id === id ? { ...s, isActive: false, updatedAt: Date.now() } : s
      ),
    }));
  },

  restoreSubject: (id) => {
    set((state) => ({
      subjects: state.subjects.map((s) =>
        s.id === id ? { ...s, isActive: true, updatedAt: Date.now() } : s
      ),
    }));
  },

  addSubjectSlot: (data) => {
    const id = generateId('slot');
    const now = Date.now();
    const newSlot: CpSubjectSlot = {
      ...data,
      id,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ subjectSlots: [...state.subjectSlots, newSlot] }));
    return id;
  },

  updateSubjectSlot: (id, updates) => {
    set((state) => ({
      subjectSlots: state.subjectSlots.map((ss) =>
        ss.id === id ? { ...ss, ...updates, updatedAt: Date.now() } : ss
      ),
    }));
  },

  addSubjectToSlot: (slotId, subjectId) => {
    const id = generateId('sss');
    const newLink: CpSubjectSlotSubject = {
      id,
      subjectSlotId: slotId,
      subjectId,
      createdAt: Date.now(),
    };
    set((state) => ({ subjectSlotSubjects: [...state.subjectSlotSubjects, newLink] }));
  },

  removeSubjectFromSlot: (slotId, subjectId) => {
    set((state) => ({
      subjectSlotSubjects: state.subjectSlotSubjects.filter(
        (sss) => !(sss.subjectSlotId === slotId && sss.subjectId === subjectId)
      ),
    }));
  },

  // ============================================================================
  // Mutations - Teachers
  // ============================================================================

  addTeacher: (data) => {
    const id = generateId('teacher');
    const now = Date.now();
    const newTeacher: CpTeacher = {
      ...data,
      id,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ teachers: [...state.teachers, newTeacher] }));
    return id;
  },

  updateTeacher: (id, updates) => {
    set((state) => ({
      teachers: state.teachers.map((t) =>
        t.id === id ? { ...t, ...updates, updatedAt: Date.now() } : t
      ),
    }));
  },

  archiveTeacher: (id) => {
    set((state) => ({
      teachers: state.teachers.map((t) =>
        t.id === id ? { ...t, isActive: false, updatedAt: Date.now() } : t
      ),
    }));
  },

  restoreTeacher: (id) => {
    set((state) => ({
      teachers: state.teachers.map((t) =>
        t.id === id ? { ...t, isActive: true, updatedAt: Date.now() } : t
      ),
    }));
  },

  // ============================================================================
  // Mutations - Groupings
  // ============================================================================

  addBand: (data) => {
    const id = generateId('band');
    const now = Date.now();
    const newBand: CpBand = { ...data, id, createdAt: now, updatedAt: now };
    set((state) => ({ bands: [...state.bands, newBand] }));
    return id;
  },

  updateBand: (id, updates) => {
    set((state) => ({
      bands: state.bands.map((b) =>
        b.id === id ? { ...b, ...updates, updatedAt: Date.now() } : b
      ),
    }));
  },

  deleteBand: (id) => {
    set((state) => ({
      bands: state.bands.filter((b) => b.id !== id),
      sets: state.sets.map((s) => (s.bandId === id ? { ...s, bandId: undefined } : s)),
      classes: state.classes.map((c) => (c.bandId === id ? { ...c, bandId: undefined } : c)),
    }));
  },

  addSet: (data) => {
    const id = generateId('set');
    const now = Date.now();
    const newSet: CpSet = { ...data, id, createdAt: now, updatedAt: now };
    set((state) => ({ sets: [...state.sets, newSet] }));
    return id;
  },

  updateSet: (id, updates) => {
    set((state) => ({
      sets: state.sets.map((s) =>
        s.id === id ? { ...s, ...updates, updatedAt: Date.now() } : s
      ),
    }));
  },

  deleteSet: (id) => {
    set((state) => ({
      sets: state.sets.filter((s) => s.id !== id),
      classes: state.classes.map((c) => (c.setId === id ? { ...c, setId: undefined } : c)),
    }));
  },

  addClass: (data) => {
    const id = generateId('class');
    const now = Date.now();
    const newClass: CpClass = { ...data, id, createdAt: now, updatedAt: now };
    set((state) => ({ classes: [...state.classes, newClass] }));
    return id;
  },

  updateClass: (id, updates) => {
    set((state) => ({
      classes: state.classes.map((c) =>
        c.id === id ? { ...c, ...updates, updatedAt: Date.now() } : c
      ),
    }));
  },

  deleteClass: (id) => {
    set((state) => ({ classes: state.classes.filter((c) => c.id !== id) }));
  },

  // ============================================================================
  // Mutations - Option Blocks
  // ============================================================================

  addOptionBlock: (data) => {
    const id = generateId('ob');
    const now = Date.now();
    const newBlock: CpOptionBlock = { ...data, id, createdAt: now, updatedAt: now };
    set((state) => ({ optionBlocks: [...state.optionBlocks, newBlock] }));
    return id;
  },

  updateOptionBlock: (id, updates) => {
    set((state) => ({
      optionBlocks: state.optionBlocks.map((ob) =>
        ob.id === id ? { ...ob, ...updates, updatedAt: Date.now() } : ob
      ),
    }));
  },

  deleteOptionBlock: (id) => {
    set((state) => ({
      optionBlocks: state.optionBlocks.filter((ob) => ob.id !== id),
      optionBlockSubjects: state.optionBlockSubjects.filter((obs) => obs.optionBlockId !== id),
    }));
  },

  addSubjectToOptionBlock: (blockId, subjectId, maxStudents, teacherId) => {
    const id = generateId('obs');
    const newLink: CpOptionBlockSubject = {
      id,
      optionBlockId: blockId,
      subjectId,
      maxStudents,
      teacherId,
      createdAt: Date.now(),
    };
    set((state) => ({ optionBlockSubjects: [...state.optionBlockSubjects, newLink] }));
  },

  removeSubjectFromOptionBlock: (blockId, subjectId) => {
    set((state) => ({
      optionBlockSubjects: state.optionBlockSubjects.filter(
        (obs) => !(obs.optionBlockId === blockId && obs.subjectId === subjectId)
      ),
    }));
  },

  // ============================================================================
  // Mutations - Curriculum Plans
  // ============================================================================

  addCurriculumPlan: (data) => {
    const id = generateId('cp');
    const now = Date.now();
    const newPlan: CpCurriculumPlan = { ...data, id, createdAt: now, updatedAt: now };
    set((state) => ({ curriculumPlans: [...state.curriculumPlans, newPlan] }));
    return id;
  },

  updateCurriculumPlan: (id, updates) => {
    set((state) => ({
      curriculumPlans: state.curriculumPlans.map((cp) =>
        cp.id === id ? { ...cp, ...updates, updatedAt: Date.now() } : cp
      ),
    }));
  },

  deleteCurriculumPlan: (id) => {
    set((state) => ({ curriculumPlans: state.curriculumPlans.filter((cp) => cp.id !== id) }));
  },

  // ============================================================================
  // Bulk Operations
  // ============================================================================

  resetAllData: () => {
    set({
      academicYears: mockCpAcademicYears,
      terms: mockCpTerms,
      keyStages: mockCpKeyStages,
      yearGroups: mockCpYearGroups,
      timetableCycles: mockCpTimetableCycles,
      periods: mockCpPeriods,
      subjectCategories: mockCpSubjectCategories,
      subjects: mockCpSubjects,
      subjectSlots: mockCpSubjectSlots,
      subjectSlotSubjects: mockCpSubjectSlotSubjects,
      staffRoles: mockCpStaffRoles,
      teachers: mockCpTeachers,
      teacherRoles: mockCpTeacherRoles,
      teacherSubjects: mockCpTeacherSubjects,
      roomTypes: mockCpRoomTypes,
      rooms: mockCpRooms,
      bands: mockCpBands,
      sets: mockCpSets,
      classes: mockCpClasses,
      optionBlocks: mockCpOptionBlocks,
      optionBlockSubjects: mockCpOptionBlockSubjects,
      rotationGroups: mockCpRotationGroups,
      rotationGroupSubjects: mockCpRotationGroupSubjects,
      curriculumPlans: mockCpCurriculumPlans,
    });
  },

  importData: (data) => {
    set((state) => ({ ...state, ...data }));
  },
}));

export default useCurriculumPlanningStore;
